// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as TrustedDomainCommands from '@app/features/trusted_domain/commands/TrustedDomainCommands';
import TrustedDomain from '@app/features/trusted_domain/state/TrustedDomain';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/Switch';
import UserSettings from '@app/features/user/state/UserSettings';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';
export const LinksTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const trustAll = TrustedDomain.trustAllDomains;
	const trustedCount = TrustedDomain.getTrustedDomainsCount();
	const description = useMemo(() => {
		if (trustAll) {
			return t`All external links are trusted. Warnings will not be shown.`;
		}
		if (trustedCount > 0) {
			return plural(
				{count: trustedCount},
				{
					one: 'You have # trusted domain. Add more by checking the box when visiting external links.',
					other: 'You have # trusted domains. Add more by checking the box when visiting external links.',
				},
			);
		}
		return t`When enabled, no external link warnings will be shown. This is less secure.`;
	}, [trustAll, trustedCount, t]);
	const handleTrustAllChange = useCallback(
		(value: boolean) => {
			if (value) {
				ModalCommands.push(
					ModalCommands.modal(() => (
						<ConfirmModal
							title={t`Trust all external links?`}
							description={
								<Trans>
									This will trust all external links and skip the warning for every domain. Your existing trusted
									domains will be replaced. This is less secure.
								</Trans>
							}
							primaryText={t`Trust All`}
							primaryVariant="danger-primary"
							onPrimary={async () => {
								await TrustedDomainCommands.setTrustAllDomains(true);
							}}
						/>
					)),
				);
			} else {
				ModalCommands.push(
					ModalCommands.modal(() => (
						<ConfirmModal
							title={t`Stop trusting all links?`}
							description={
								<Trans>
									External link warnings will be shown again. You will need to add trusted domains individually.
								</Trans>
							}
							primaryText={t`Disable Trust All`}
							onPrimary={async () => {
								await TrustedDomainCommands.setTrustAllDomains(false);
							}}
						/>
					)),
				);
			}
		},
		[t],
	);
	const sanitizeUrls = UserSettings.getSanitizeUrls();
	const handleSanitizeUrlsChange = useCallback((value: boolean) => {
		void UserSettings.setSanitizeUrls(value);
	}, []);
	return (
		<>
			<Switch
				label={t`Trust all external links`}
				description={description}
				value={trustAll}
				onChange={handleTrustAllChange}
			/>
			<Switch
				label={t`Strip Tracking Parameters from URLs`}
				description={t`Automatically remove tracking parameters (like utm_source, fbclid, gclid) from URLs in messages you send. Cleans the link before it reaches anyone else.`}
				value={sanitizeUrls}
				onChange={handleSanitizeUrlsChange}
			/>
		</>
	);
});
